import { convertSearchParamsToObject } from "helpers";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Button, DateRangeInput, Input, Select } from "ui";

import {
  ACTION_FILTER_SEARCH_PARAMETER,
  ADMIN_ID_FILTER_SEARCH_PARAMETER,
  AUDIT_ENTITY_TYPE_OPTIONS,
  DATE_RANGE_FILTER_SEARCH_PARAMETER,
  EMPTY_AUDIT_LOGS_FILTER_FORM_VALUES,
  ENTITY_ID_FILTER_SEARCH_PARAMETER,
  ENTITY_TYPE_FILTER_SEARCH_PARAMETER
} from "../../../constants";
import {
  AuditLogsFilterFormProps,
  FilterFormValues
} from "./AuditLogsFilterForm.types";

import styles from "./AuditLogsFilterForm.module.scss";

const AuditLogsFilterForm = ({
  formId,
  onSubmit,
  isLoading
}: AuditLogsFilterFormProps) => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const { control, handleSubmit, reset } = useForm<FilterFormValues>({
    defaultValues: EMPTY_AUDIT_LOGS_FILTER_FORM_VALUES
  });

  useEffect(() => {
    const newValues = convertSearchParamsToObject(searchParams);

    if (newValues?.dateRange && !Array.isArray(newValues.dateRange)) {
      newValues.dateRange = [newValues.dateRange];
    }

    reset({ ...EMPTY_AUDIT_LOGS_FILTER_FORM_VALUES, ...newValues });
  }, [reset, searchParams]);

  const clearFilterForm = () => {
    searchParams.delete(ACTION_FILTER_SEARCH_PARAMETER);
    searchParams.delete(ENTITY_TYPE_FILTER_SEARCH_PARAMETER);
    searchParams.delete(ENTITY_ID_FILTER_SEARCH_PARAMETER);
    searchParams.delete(ADMIN_ID_FILTER_SEARCH_PARAMETER);
    searchParams.delete(DATE_RANGE_FILTER_SEARCH_PARAMETER);

    setSearchParams(searchParams);
    reset(EMPTY_AUDIT_LOGS_FILTER_FORM_VALUES);
  };

  return (
    <form
      id={formId}
      className={styles["filter-form"]}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={styles["filter-form__header"]}>
        <div className={styles["filter-form__header--title"]}>
          {t("Filter by")}
        </div>
        <div
          onClick={clearFilterForm}
          className={styles["filter-form__header--clear"]}
        >
          {t("Clear all filters")}
        </div>
      </div>

      <Controller
        control={control}
        name="action"
        render={({ field: { value, onChange } }) => (
          <Input
            value={value ?? ""}
            onChange={onChange}
            label={t("Action")}
            placeholder={t("e.g. Earning.pay")}
          />
        )}
      />

      <Controller
        control={control}
        name="entityType"
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("Entity type")}
            placeholder={t("Any entity type")}
            value={value ?? ""}
            onChange={onChange}
            options={AUDIT_ENTITY_TYPE_OPTIONS}
          />
        )}
      />

      <Controller
        control={control}
        name="entityId"
        render={({ field: { value, onChange } }) => (
          <Input
            value={value ?? ""}
            onChange={onChange}
            label={t("Entity ID")}
            placeholder={t("Entity UUID")}
          />
        )}
      />

      <Controller
        control={control}
        name="adminId"
        render={({ field: { value, onChange } }) => (
          <Input
            value={value ?? ""}
            onChange={onChange}
            label={t("Admin ID")}
            placeholder={t("Admin UUID")}
          />
        )}
      />

      <div className={styles["filter-form__date"]}>
        <h4 className={styles["filter-form__date--title"]}>
          {t("Date (from/to)")}
        </h4>
        <Controller
          control={control}
          name="dateRange"
          render={({ field: { value, onChange } }) => (
            <DateRangeInput value={value} onChange={onChange} />
          )}
        />
      </div>

      <Button
        type="submit"
        loading={isLoading}
        className={styles["filter-form__button"]}
      >
        {t("Apply")}
      </Button>
    </form>
  );
};

export default AuditLogsFilterForm;
